import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Send, CheckCircle, AlertCircle, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/contacts/contact`,
        {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          message: formData.message
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000
        }
      );
      
      if (response.status === 201 && response.data.success) {
        setSubmitStatus('success');
        setMessage('Thank you for reaching out! We\'ll get back to you within 24-48 hours.');
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          message: ''
        });
      }
      
    } catch (error) {
      setSubmitStatus('error');
      if (axios.isAxiosError(error)) {
        setMessage(
          error.response?.data?.detail || 'Something went wrong. Please try again or email us directly.'
        );
      } else {
        setMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-[#f7f5f2] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center space-x-2 bg-[#a8b5a0]/10 px-4 py-2 rounded-full mb-6">
            <MessageSquare className="h-5 w-5 text-[#a8b5a0]" />
            <span className="text-sm font-medium text-[#736c64]">We're Here to Help</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#1e1919] mb-6">
            Get in Touch
          </h1>
          <p className="text-xl text-[#736c64] max-w-2xl mx-auto mb-4">
            Questions about your Hearthlowe kit or product? We're here to help.
          </p>
          <div className="bg-[#a8b5a0]/10 rounded-lg p-4 max-w-2xl mx-auto border border-[#a8b5a0]/30">
            <p className="text-[#1e1919] font-medium">
              <strong>Note:</strong> This form is for product-related questions only. 
              For sourdough baking tips and techniques, please visit our <a href="/getting-started" className="text-[#a8b5a0] hover:text-[#8fa087] underline">Getting Started</a>, <a href="/recipes" className="text-[#a8b5a0] hover:text-[#8fa087] underline">Recipes</a>, <a href="/troubleshooting" className="text-[#a8b5a0] hover:text-[#8fa087] underline">Troubleshooting</a>, and <a href="/faq" className="text-[#a8b5a0] hover:text-[#8fa087] underline">FAQ</a> pages.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#a8b5a0]/20 rounded-lg flex items-center justify-center">
                      <Mail className="h-6 w-6 text-[#a8b5a0]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1e1919] mb-2">Email Us</h3>
                      <a 
                        href="mailto:support@myhearthlowe.com" 
                        className="text-[#a8b5a0] hover:text-[#8fa087] transition-colors"
                      >
                        support@myhearthlowe.com
                      </a>
                      <p className="text-sm text-[#736c64] mt-2">
                        We typically respond within 24-48 hours.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-[#1e1919] mb-3">Quick Links</h3>
                  <ul className="space-y-2">
                    <li>
                      <a href="/faq" className="text-[#736c64] hover:text-[#a8b5a0] transition-colors">
                        Frequently Asked Questions
                      </a>
                    </li>
                    <li>
                      <a href="/troubleshooting" className="text-[#736c64] hover:text-[#a8b5a0] transition-colors">
                        Troubleshooting Guide
                      </a>
                    </li>
                    <li>
                      <a href="/care" className="text-[#736c64] hover:text-[#a8b5a0] transition-colors">
                        Care Guides
                      </a>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <div className="bg-[#f7f5f2] rounded-xl p-6 border border-[#e8e6e1]">
                <p className="text-sm text-[#736c64]">
                  <strong>For Amazon Order Issues:</strong> Please contact Amazon customer service directly for order issues, returns, or shipping questions.
                </p>
                <p className="text-sm text-[#736c64] mt-2">
                  <strong>For Baking Help:</strong> Check our <a href="/troubleshooting" className="text-[#a8b5a0] hover:text-[#8fa087] underline">Troubleshooting</a> and <a href="/faq" className="text-[#a8b5a0] hover:text-[#8fa087] underline">FAQ</a> pages for common sourdough questions.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-[#1e1919] mb-6">
                    Product Questions or Concerns
                  </h2>
                  <p className="text-[#736c64] mb-6">
                    Have questions about your Hearthlowe kit, need help with a tool, or have product feedback? Let us know below.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-[#1e1919] mb-2">
                          First Name *
                        </label>
                        <Input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          disabled={isSubmitting}
                          placeholder="John"
                          className="border-[#e8e6e1] focus:border-[#a8b5a0] focus:ring-[#a8b5a0]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#1e1919] mb-2">
                          Last Name *
                        </label>
                        <Input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          disabled={isSubmitting}
                          placeholder="Doe"
                          className="border-[#e8e6e1] focus:border-[#a8b5a0] focus:ring-[#a8b5a0]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#1e1919] mb-2">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        placeholder="john@example.com"
                        className="border-[#e8e6e1] focus:border-[#a8b5a0] focus:ring-[#a8b5a0]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#1e1919] mb-2">
                        Your Question or Concern *
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        placeholder="Tell us about your product question or concern..."
                        rows={6}
                        className="border-[#e8e6e1] focus:border-[#a8b5a0] focus:ring-[#a8b5a0] resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#a8b5a0] hover:bg-[#8fa087] text-white py-6 text-lg transition-all"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </form>

                  {submitStatus === 'success' && (
                    <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3 animate-in fade-in slide-in-from-top duration-300">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-green-800 text-sm">{message}</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 animate-in fade-in slide-in-from-top duration-300">
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-red-800 text-sm">{message}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
